import { useRef, useEffect } from "react";

export function useDrag(
    callbacks: {
        onDragUp?: (delta: number) => void;
        onDragDown?: (delta: number) => void;
        onDragLeft?: (delta: number) => void;
        onDragRight?: (delta: number) => void;
    },
    threshold = 10
) {
    const ref = useRef(null);
    const startYRef = useRef<number | null>(null);
    const startXRef = useRef<number | null>(null);

    const handlePointerStart = (e: PointerEvent) => {
        startXRef.current = e.clientX;
        startYRef.current = e.clientY;
    };

    const handlePointerMove = (e: PointerEvent) => {
        if (startYRef.current === null || startXRef.current === null) return;

        const element = ref?.current as HTMLElement | null;
        if (!element) return;

        if (element.style.cursor !== "grabbing") {
            element.style.setProperty("cursor", "grabbing");
            element
                .querySelectorAll("*")
                .forEach((el) => (el as HTMLElement).style.setProperty("pointer-events", "none"));
        }

        const deltaY = e.clientY - startYRef.current;
        const deltaX = e.clientX - startXRef.current;

        if (deltaY > 0 && deltaY > threshold && callbacks.onDragDown) callbacks.onDragDown(deltaY);
        if (deltaY < 0 && Math.abs(deltaY) > threshold && callbacks.onDragUp)
            callbacks.onDragUp(deltaY);
        if (deltaX > 0 && deltaX > threshold && callbacks.onDragRight)
            callbacks.onDragRight(deltaX);
        if (deltaX < 0 && Math.abs(deltaX) > threshold && callbacks.onDragLeft)
            callbacks.onDragLeft(deltaX);
    };

    const handlePointerStop = () => {
        startYRef.current = null;
        startXRef.current = null;

        const element = ref?.current as HTMLElement | null;
        if (!element) return;
        element.style.removeProperty("cursor");
        element
            .querySelectorAll("*")
            .forEach((el) => (el as HTMLElement).style.removeProperty("pointer-events"));
    };

    useEffect(() => {
        const element = ref?.current as HTMLElement | null;
        if (!element) return;

        element.addEventListener("pointerdown", handlePointerStart);
        element.addEventListener("pointermove", handlePointerMove);
        document.addEventListener("pointerup", handlePointerStop);

        return () => {
            element.removeEventListener("pointerdown", handlePointerStart);
            element.removeEventListener("pointermove", handlePointerMove);
            document.removeEventListener("pointerup", handlePointerStop);
        };
    }, [ref, callbacks, threshold]);

    return { ref };
}
